import { createTheme } from '@mui/material/styles';

const createAppTheme = (darkMode, isArabic) => createTheme({
    palette: {
        mode: darkMode ? 'dark' : 'light',
        primary: {
            main: '#A3F22F',
            light: '#B8F55A',
            dark: '#7DC020',
        },
        secondary: {
            main: '#905A01',
            light: '#B87202',
            dark: '#6B4300',
        },
        background: {
            default: '#14220E',   // #0A0A0A blended ~20% with #3A811D
            paper: '#192713',     // #111111 blended ~20% with #3A811D
        },
        text: {
            primary: '#FFFFFF',
            secondary: '#B0B0B0',
        },
        success: {
            main: '#4caf50',
        },
        error: {
            main: '#f44336',
        },
    },
    direction: isArabic ? 'rtl' : 'ltr',
    typography: {
        fontFamily: '"Noto Kufi Arabic", sans-serif',
        h1: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontWeight: 600, fontSize: '2.5rem', '@media (max-width:600px)': { fontSize: '2rem' } },
        h2: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontWeight: 600, fontSize: '2.25rem', '@media (max-width:600px)': { fontSize: '1.75rem' } },
        h3: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontWeight: 600, fontSize: '2rem', '@media (max-width:600px)': { fontSize: '1.5rem' } },
        h4: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontWeight: 600, fontSize: '2rem', letterSpacing: '-0.02em', '@media (max-width:600px)': { fontSize: '1.5rem' } },
        h5: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontWeight: 600, fontSize: '1.5rem', '@media (max-width:600px)': { fontSize: '1.25rem' } },
        h6: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontWeight: 500, fontSize: '1.25rem', '@media (max-width:600px)': { fontSize: '1.125rem' } },
        subtitle1: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontWeight: 500, fontSize: '1rem', '@media (max-width:600px)': { fontSize: '0.875rem' } },
        subtitle2: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontWeight: 500, fontSize: '0.875rem', '@media (max-width:600px)': { fontSize: '0.8125rem' } },
        body1: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontSize: '1rem', lineHeight: 1.6, '@media (max-width:600px)': { fontSize: '0.875rem', lineHeight: 1.5 } },
        body2: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontSize: '0.875rem', lineHeight: 1.5, '@media (max-width:600px)': { fontSize: '0.8125rem' } },
        button: { fontFamily: '"Noto Kufi Arabic", sans-serif', fontSize: '0.875rem', fontWeight: 500, '@media (max-width:600px)': { fontSize: '1rem' } },
    },
    shape: { borderRadius: 12 },
    breakpoints: {
        values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    fontFamily: '"Noto Kufi Arabic", sans-serif',
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontWeight: 500,
                    padding: '12px 24px',
                    minHeight: '44px',
                    '@media (max-width:600px)': { padding: '14px 24px', minHeight: '48px', fontSize: '1rem' },
                },
                small: { padding: '8px 16px', minHeight: '36px', '@media (max-width:600px)': { padding: '10px 18px', minHeight: '40px' } },
                large: { padding: '16px 32px', minHeight: '48px', '@media (max-width:600px)': { padding: '18px 32px', minHeight: '56px' } },
                contained: {
                    background: 'linear-gradient(135deg, #A3F22F 0%, #7DC020 100%)',
                    color: '#000',
                    '&:hover': { background: 'linear-gradient(135deg, #B8F55A 0%, #A3F22F 100%)' },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    fontFamily: '"Noto Kufi Arabic", sans-serif',
                    background: 'linear-gradient(135deg, rgba(20, 34, 14, 0.92), rgba(25, 42, 18, 0.92))',
                    borderRadius: 16,
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    width: '100%',
                    height: '100%',
                    '@media (max-width:600px)': { borderRadius: 12, '&:hover': { transform: 'none' } },
                    '@media (min-width:601px)': {
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            border: '1px solid rgba(163, 242, 47, 0.3)',
                            boxShadow: '0 8px 32px rgba(163, 242, 47, 0.08)',
                        },
                    },
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    fontFamily: '"Noto Kufi Arabic", sans-serif',
                    padding: '24px',
                    '@media (max-width:600px)': { padding: '16px' },
                    '&:last-child': { paddingBottom: '24px', '@media (max-width:600px)': { paddingBottom: '16px' } },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontFamily: '"Noto Kufi Arabic", sans-serif',
                    borderRadius: '6px',
                    fontWeight: 500,
                    height: '32px',
                    '@media (max-width:600px)': { height: '36px', fontSize: '0.8125rem' },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    fontFamily: '"Noto Kufi Arabic", sans-serif',
                    background: 'rgba(14, 26, 9, 0.97)',
                    backdropFilter: 'blur(20px)',
                    borderBottom: '1px solid rgba(163, 242, 47, 0.12)',
                    '@media (max-width:600px)': { paddingTop: 'env(safe-area-inset-top)' },
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    fontFamily: '"Noto Kufi Arabic", sans-serif',
                    minHeight: '64px',
                    '@media (max-width:600px)': { minHeight: '56px', paddingLeft: '12px', paddingRight: '12px' },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontFamily: '"Noto Kufi Arabic", sans-serif',
                    textTransform: 'none',
                    fontWeight: 500,
                    minHeight: '48px',
                    '@media (max-width:600px)': { minHeight: '56px', fontSize: '0.875rem', minWidth: 'auto', padding: '12px 8px' },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: { '@media (max-width:600px)': { '& .MuiTabs-scroller': { overflowX: 'auto !important' } } },
                indicator: { backgroundColor: '#A3F22F' },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    fontFamily: '"Noto Kufi Arabic", sans-serif',
                    '@media (max-width:600px)': { margin: '16px', width: 'calc(100% - 32px)', maxHeight: 'calc(100% - 32px)' },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    fontFamily: '"Noto Kufi Arabic", sans-serif',
                    '& .MuiInputBase-input': { '@media (max-width:600px)': { fontSize: '1rem', padding: '14px' } },
                    '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#A3F22F' } },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#A3F22F' },
                },
            },
        },
        MuiGrid: {
            styleOverrides: {
                container: {
                    '@media (max-width:600px)': {
                        '&.MuiGrid-spacing-xs-2 > .MuiGrid-item': { paddingTop: '8px', paddingLeft: '8px' },
                        '&.MuiGrid-spacing-xs-3 > .MuiGrid-item': { paddingTop: '12px', paddingLeft: '12px' },
                    },
                },
            },
        },
        MuiContainer: {
            styleOverrides: { root: { '@media (max-width:600px)': { paddingLeft: '16px', paddingRight: '16px' } } },
        },
        MuiAlert: {
            styleOverrides: {
                root: { fontFamily: '"Noto Kufi Arabic", sans-serif', borderRadius: '8px', '@media (max-width:600px)': { fontSize: '0.875rem', padding: '12px 16px' } },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: { fontFamily: '"Noto Kufi Arabic", sans-serif', '@media (max-width:600px)': { '& .MuiTableCell-root': { padding: '8px', fontSize: '0.875rem' } } },
            },
        },
        MuiTableCell: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiTableHead: { styleOverrides: { root: { '& .MuiTableCell-root': { fontFamily: '"Noto Kufi Arabic", sans-serif' } } } },
        MuiDrawer: { styleOverrides: { paper: { fontFamily: '"Noto Kufi Arabic", sans-serif', '@media (max-width:600px)': { width: '280px' } } } },
        MuiMenuItem: {
            styleOverrides: {
                root: { fontFamily: '"Noto Kufi Arabic", sans-serif', minHeight: '48px', '@media (max-width:600px)': { minHeight: '56px', fontSize: '1rem', padding: '16px' } },
            },
        },
        MuiMenu: { styleOverrides: { paper: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiListItem: {
            styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif', '@media (max-width:600px)': { paddingTop: '12px', paddingBottom: '12px' } } },
        },
        MuiListItemButton: {
            styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif', minHeight: '48px', '@media (max-width:600px)': { minHeight: '56px', paddingLeft: '16px', paddingRight: '16px' } } },
        },
        MuiListItemText: {
            styleOverrides: {
                root: { '& .MuiTypography-root': { fontFamily: '"Noto Kufi Arabic", sans-serif' } },
                primary: { fontFamily: '"Noto Kufi Arabic", sans-serif' },
                secondary: { fontFamily: '"Noto Kufi Arabic", sans-serif' },
            },
        },
        MuiTypography: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiInputBase: {
            styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' }, input: { fontFamily: '"Noto Kufi Arabic", sans-serif' } },
        },
        MuiSelect: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' }, select: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiFormLabel: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiInputLabel: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif', '&.Mui-focused': { color: '#A3F22F' } } } },
        MuiFormHelperText: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiSnackbar: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiSnackbarContent: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiTooltip: { styleOverrides: { tooltip: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiBreadcrumbs: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiAccordion: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiAccordionSummary: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiAccordionDetails: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiPagination: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
        MuiPaginationItem: { styleOverrides: { root: { fontFamily: '"Noto Kufi Arabic", sans-serif' } } },
    },
});

export default createAppTheme;